'use client';

import { useTranslations } from 'next-intl';
import { DocsLinks } from '@/components/how-it-works/docs-links';
import { ChartUpIcon, Coins01Icon, Rocket01Icon } from '@/components/ui/icon/glyphs';
import { InfoModal } from '@/components/ui/info-modal';

interface BackerRewardsHowItWorksModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Show the "figure shown is an estimate" callout. True on the proposal-detail
   * projection card (with its volume slider); pass false on the portfolio
   * rollup, where the earned amounts are real accrued fees, not an estimate.
   */
  showEstimateNote?: boolean;
}

/**
 * Explains the "Backer rewards" feature. Used both on the proposal detail page
 * (a projection that scales with hypothetical volume via the card's slider and
 * the viewer's share of total backing — see `projectReward` in
 * `proposal-backer-rewards-panel.tsx`) and on the portfolio backer-rewards
 * rollup card (`backer-rewards-card-view.tsx`), which shows real accrued fees
 * and therefore passes `showEstimateNote={false}`.
 */
export function BackerRewardsHowItWorksModal({
  open,
  onOpenChange,
  showEstimateNote = true,
}: BackerRewardsHowItWorksModalProps): React.ReactNode {
  const t = useTranslations('backer_rewards_how_it_works');

  return (
    <InfoModal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={t('title')}
      description={t('description')}
      footer={<InfoModal.Footer cta={t('got_it')} onClose={(): void => onOpenChange(false)} />}
    >
      <InfoModal.IconList>
        <InfoModal.IconItem
          icon={Rocket01Icon}
          title={t('early_title')}
          description={t('early_description')}
        />
        <InfoModal.IconItem
          icon={Coins01Icon}
          title={t('fees_title')}
          description={t('fees_description')}
        />
        <InfoModal.IconItem
          icon={ChartUpIcon}
          title={t('volume_title')}
          description={t('volume_description')}
        />
      </InfoModal.IconList>
      {showEstimateNote ? <InfoModal.Callout>{t('info')}</InfoModal.Callout> : null}
      <DocsLinks docsPath="/guides/backer-rewards" />
    </InfoModal>
  );
}
